import { Plan, PlanProductEntry } from 'screens/Plans/ConvertPlanModalV2/types';
import { OperationPropertyVO } from 'screens/Applications&WorkOrders/Components/hooks/useOperationProperties';
import {
  Property,
  Product,
  ProductQuantityType
} from 'screens/Applications&WorkOrders/Applications/Hooks/useApplication';
import { getPlanProperties } from 'screens/Plans/helpers/dataHelpers';
import generateDefaultApplicationId from 'screens/Applications&WorkOrders/StepsLayout/Helpers/generateDefaultApplicationId';
import { getInitials } from 'helpers/textHelpers';
import {
  FormValues as ApplicationFormValues,
  initialValues,
  CropZone as FormCropZone,
  RecordSourceType
} from 'screens/Applications&WorkOrders/StepsLayout/Constants/formikData';
import { PropertyLandingPageData } from 'screens/Applications&WorkOrders/Components/types';
import { Maths } from 'helpers/maths';

const PERCENTAGE_BASE = 100;

/**
 * Transforms plan crop zones to OperationPropertyVO format
 */
export const transformPlanCropZonesToOperationPropertyVO = (
  plan: Plan,
  propertyLandingPageData: PropertyLandingPageData | undefined,
  fieldsAndAreasGeoJSONCollection: any
): OperationPropertyVO[] => {
  if (!plan?.cropZones?.length || !propertyLandingPageData) {
    return [];
  }

  const result: OperationPropertyVO[] = [];

  plan.cropZones.forEach(planCropZone => {
    const planProperties = getPlanProperties(
      propertyLandingPageData,
      [planCropZone.cropZoneIdValue],
      fieldsAndAreasGeoJSONCollection,
      plan
    );

    if (!planProperties?.[0]) {
      return;
    }

    const cropZoneData = planProperties[0];
    const area = cropZoneData.reportedArea || cropZoneData.boundaryArea || 0;
    const appliedArea = planCropZone.areaValue || 0;
    let appliedAreaPercent = 0;
    // Nullish check, not truthy — a crop zone explicitly saved at exactly
    // 0% coverage is a real, finite value that must carry over as 0%, not
    // fall through to the area-ratio recompute below.
    if (
      planCropZone.coveragePct != null &&
      Number.isFinite(planCropZone.coveragePct)
    ) {
      appliedAreaPercent = Maths.multiplyRound(
        planCropZone.coveragePct,
        PERCENTAGE_BASE
      );
    } else if (area) {
      appliedAreaPercent = Maths.multiplyRound(
        Maths.divideSafeRound(appliedArea, Maths.parseFloatRound(area, 2)),
        PERCENTAGE_BASE
      );
    }

    result.push({
      farmId: cropZoneData.farmId || '',
      farmName: cropZoneData.farmName || '',
      farmArchived: cropZoneData.farmArchived || false,
      fieldId: cropZoneData.fieldId || '',
      fieldName: cropZoneData.fieldName || '',
      fieldGeometry: cropZoneData.fieldGeometry,
      fieldArchived: cropZoneData.fieldArchived || false,
      cropZoneId: planCropZone.cropZoneIdValue || '',
      cropZoneName: cropZoneData.name || planCropZone.name || '',
      cropZoneGeometry: cropZoneData.geometry,
      cropZoneArea: cropZoneData.reportedArea || cropZoneData.boundaryArea || 0,
      appliedArea,
      appliedAreaPercent,
      cropZoneArchived: cropZoneData.archived || false,
      cropName: cropZoneData?.crop?.name || '',
      cropId: cropZoneData?.crop?.id || planCropZone.cropId || ''
    });
  });

  return result;
};

/**
 * Transforms plan crop zones to Property format
 */
export const transformPlanCropZonesToProperty = (plan: Plan): Property[] => {
  if (!plan?.cropZones?.length) {
    return [];
  }

  return plan.cropZones.map(planCropZone => ({
    farmId: '', // Will be populated from propertyLandingPageData if needed
    fieldId: '', // Will be populated from propertyLandingPageData if needed
    cropZoneId: planCropZone.cropZoneIdValue || '',
    areaValue: planCropZone.areaValue || 0,
    areaUnit: planCropZone.areaUnit || 'acre',
    areaType: planCropZone.reportedArea ? 'REPORTED' : 'BOUNDARY',
    coveragePercent: plan.coveragePercent || 100
  }));
};

/**
 * Transforms PlanProductEntry to Product format for API
 */
export const transformPlanProductEntryToProduct = (
  planProduct: PlanProductEntry
): Product => {
  return {
    productId: planProduct.productId,
    productName: planProduct.productName,
    productQuantityType: ProductQuantityType.ratePerAcre, // Default to rate per area
    priceValue: planProduct.specificCostValue,
    priceUnit: planProduct.specificCostUnit,
    totalCostValue: planProduct.totalProductCost,
    totalCostUnit: planProduct.currency,
    appliedAreaValue: planProduct.areaValue,
    appliedAreaUnit: planProduct.areaUnit,
    appliedAreaCoveragePercent: Maths.multiplyRound(
      planProduct.coveragePercent,
      PERCENTAGE_BASE
    ),
    totalProductValue: planProduct.totalProductValue,
    totalProductUnit: planProduct.totalProductUnit,
    ratePerAreaValue: planProduct.productRateValue,
    ratePerAreaUnit: planProduct.productRateUnit,
    ratePerTankValue: 0,
    ratePerTankUnit: planProduct.productRateUnit,
    applicationMethod: undefined,
    targetPestNameIds: undefined,
    custom: planProduct.custom,
    isCarrier: false,
    isHidden: planProduct.hide || false
  };
};

export const convertPlanToOperationFormikData = (
  plan: Plan,
  mappedCropZones: FormCropZone[],
  mappedProducts: any[],
  totalAppliedArea: number,
  user?: { name: string }
): ApplicationFormValues => {
  const initials =
    user?.name && user.name.trim().length > 0 ? getInitials(user.name) : 'NA';

  return {
    ...initialValues,
    id: undefined, // Clear id for new application
    applicationId: generateDefaultApplicationId(initials),
    notes: plan.notes,
    tagIds: plan.flagIds,
    cropZones: mappedCropZones,
    products: mappedProducts,
    totalAppliedAcreage: totalAppliedArea,
    recordSource: {
      recordSourceType: RecordSourceType.PLANS,
      recordSourceId: plan.id,
      recordSourceName: plan.name
    }
  };
};
